import { HHBox } from '@hinge-health/react-component-library';
import { Theme } from '@mui/material';
import { SystemStyleObject } from '@mui/system';
import { Route, Routes, useOutlet } from 'react-router-dom';
import { ModuleNav } from './components/module-nav';
import { MotionProvider, PageTransition } from './components/motion';
import { PARTNER_DATA_GATEWAY_ROUTES } from './constants/partner-data-gateway.constant';
import { ConfigurationCreatePage } from './pages/configuration-create-page';
import { ConfigurationDetailPage } from './pages/configuration-detail-page';
import { ConfigurationsPage } from './pages/configurations-page';
import { IngestionDetailPage } from './pages/ingestion-detail-page';
import { IngestionsPage } from './pages/ingestions-page';
import { OverviewPage } from './pages/overview-page';
import { ProviderDetailPage } from './pages/provider-detail-page';

export const PARTNER_DATA_GATEWAY_MODULE_TEST_ID =
  'partner-data-gateway-module';

/**
 * Section chrome shared by every route in the module: the nav strip stays
 * mounted across navigations so switching sections never re-animates it.
 *
 * The outlet is resolved to an element with `useOutlet()` rather than passed
 * down as `<Outlet />`. `AnimatePresence` re-renders the outgoing child while
 * it fades, and a live `<Outlet />` would re-resolve against the new
 * location — mounting the destination page inside the outgoing wrapper,
 * tearing it down, and mounting it a second time.
 */
const ModuleLayout = (): JSX.Element => {
  const outlet = useOutlet();

  return (
    <>
      <ModuleNav />
      <PageTransition>{outlet}</PageTransition>
    </>
  );
};

/**
 * Entry point for the Partner Data Gateway module.
 *
 * The host app mounts this under whatever route it chooses and is
 * responsible for wrapping it in `<PermissionsProvider>` and
 * `<BasePathProvider>` matching that mount point — see README.md for the
 * full contract.
 *
 * The root paints the theme's page background itself: the shell leaves its
 * body white, and the module's card surfaces only read as cards against the
 * warm neutral the design system specifies.
 */
export const PartnerDataGatewayModule = (): JSX.Element => (
  <HHBox
    data-testid={PARTNER_DATA_GATEWAY_MODULE_TEST_ID}
    sx={(theme: Theme): SystemStyleObject<Theme> => ({
      backgroundColor: theme.palette.background.default,
      flex: 1,
      minHeight: '100%',
    })}
  >
    <MotionProvider>
      <Routes>
        <Route element={<ModuleLayout />}>
          <Route index element={<OverviewPage />} />
          <Route
            path={PARTNER_DATA_GATEWAY_ROUTES.ingestions}
            element={<IngestionsPage />}
          />
          <Route
            path={PARTNER_DATA_GATEWAY_ROUTES.ingestionDetail}
            element={<IngestionDetailPage />}
          />
          <Route
            path={PARTNER_DATA_GATEWAY_ROUTES.providerDetail}
            element={<ProviderDetailPage />}
          />
          <Route
            path={PARTNER_DATA_GATEWAY_ROUTES.configurations}
            element={<ConfigurationsPage />}
          />
          {/*
            Declared before the dynamic sibling for readability — React Router
            v6 ranks the static `new` segment above `:configurationId` no
            matter the order.
          */}
          <Route
            path={PARTNER_DATA_GATEWAY_ROUTES.configurationCreate}
            element={<ConfigurationCreatePage />}
          />
          <Route
            path={PARTNER_DATA_GATEWAY_ROUTES.configurationDetail}
            element={<ConfigurationDetailPage />}
          />
        </Route>
      </Routes>
    </MotionProvider>
  </HHBox>
);
